// Plain-English labels for v2 trade states and reason codes. Wording is reused from the backend alert copy
// (api/engines-v2/lib/notify/copy.js) so the page and the emails/texts say the same thing. An unknown code
// never shows raw: it falls back to a generic plain label.

/** Why a rule was reached but the position was not closed (alert copy HOLD_TEXT). */
const HOLD: Record<string, string> = {
  covered_call_alerts_only: 'Covered call (alerts only): not closing.',
  alerts_only: 'Alerts only: not closing.',
  entry_unknown: 'Alerts only: not closing.',
  debit_alerts_only: 'Alerts only: not closing.',
  ratio_alerts_only: 'Alerts only: not closing.',
  engine_paused: 'Not closing: your manual engine is paused.',
  hands_off: 'Not closing: your manual engine is set to hands off.',
  suspended_v1: 'Not closing: your manual engine is on hold while v1 options trading is still on.',
  monitor_off: 'Not closing: automatic closes are switched off right now.',
  ledger_mismatch: 'Not closing: we are re-checking this position against your broker account.',
  structure_broken: 'Not closing: one leg of this position was closed outside the app, so closing the rest could leave you exposed. Please review it.',
  split_pending: 'Not closing: this position is being split into separate positions.',
};

/** Why a position needs attention (alert copy ATTENTION_TEXT, as a full phrase). */
const ATTENTION: Record<string, string> = {
  split_incomplete: "We couldn't finish splitting this position. Please review it.",
  exit_failing: 'Closing orders for it keep failing.',
  exit_qty_zero: 'Your broker account no longer shows the quantity we expected.',
  ledger_mismatch: 'It no longer matches your broker account.',
  assigned: 'It was assigned or exercised.',
  pickup_incomplete: 'We could not finish taking it over.',
  closed_on_broker_unexplained: 'It closed in your broker account without a matching order.',
  closed_at_broker_by_user: 'It was closed in your broker account outside the app.',
  intent_stuck: 'An order for it did not finish.',
  shared_contract_event: 'An expiration or assignment touched it and another position on the same contract.',
  manual_close_mismatch: 'The close in your broker account did not match what we expected.',
  manual_close_unpriced: 'It closed but we could not find the closing price.',
  manual_pnl_incomplete: 'Its profit or loss could not be fully worked out.',
  manual_pnl_unknown: 'Its profit or loss could not be worked out.',
  pickup_moving: 'We are still taking it over.',
  broker_qty_unconfirmed: 'Your broker account did not confirm its quantity.',
  settle_conflict: 'A fill for it could not be recorded.',
  cash_settlement_unpriced: 'It was cash settled and the amount is not known yet.',
};

/** Why a closed manual position needs a review (same phrases as the matching attention reasons). */
const REVIEW: Record<string, string> = {
  manual_admin_closed: 'Closed by an admin; the profit or loss is pending review.',
  manual_pnl_unknown: ATTENTION.manual_pnl_unknown,
  manual_pnl_incomplete: ATTENTION.manual_pnl_incomplete,
  manual_close_mismatch: ATTENTION.manual_close_mismatch,
  manual_close_unpriced: ATTENTION.manual_close_unpriced,
};

/** Common close reasons on closed rows. */
const CLOSE: Record<string, string> = {
  profit_target: 'Profit target reached',
  stop_loss: 'Stop loss reached',
  stop_loss_hard: 'Hard stop reached',
  stop_loss_hard_underlying: 'Hard stop reached',
  expiration_close: 'Expiration-day close',
  pre_expiry_itm: 'Expiration-day close',
  admin_manual: 'Closed by an admin',
  split: 'Split into single legs',
  expired: 'Expired',
  closed_at_broker: 'Closed in your broker account',
  manual_engine_retired: 'Manual engine retired',
};

const STATE: Record<string, string> = {
  open: 'OPEN', pending: 'PENDING', closing: 'CLOSING', closed: 'CLOSED', expired: 'EXPIRED', failed: 'FAILED', cancelled: 'CANCELLED',
  needs_attention: 'NEEDS ATTENTION', submitted: 'SUBMITTED',
};

const pick = (map: Record<string, string>, code: string | null | undefined, generic: string) => (code ? map[code] || generic : '');

export const holdReasonLabel = (code?: string | null) => pick(HOLD, code, 'Not closing right now.');
export const attentionReasonLabel = (code?: string | null) => pick(ATTENTION, code, 'This position needs a check.');
export const reviewReasonLabel = (code?: string | null) => pick(REVIEW, code, 'This position needs a review.');
/** Close reason on a closed row: review reasons, then common ones; unknown codes read "Closed". */
export const closeReasonLabel = (code?: string | null) => (code ? REVIEW[code] || CLOSE[code] || 'Closed' : '');
/** Hold line for a rule that actually triggered but did not close (lastExitEval HOLD with wouldHave). Routine HOLDs give ''. */
export function holdLine(ev?: { action?: string; reason?: string | null; wouldHave?: string | null } | null): string {
  if (!ev || ev.action !== 'HOLD' || !ev.wouldHave) return '';
  const what = (CLOSE[ev.wouldHave] || 'Exit rule reached').toLowerCase();
  return `Would close (${what}) - ${holdReasonLabel(ev.reason)}`;
}
export const tradeStateLabel = (code?: string | null) => (code ? STATE[code] || 'UPDATING' : '');

/** Short note beside a P&L figure when it is not final. */
export function pnlNote(t: { pnlSource?: string | null; pnlIncomplete?: boolean }): string | null {
  if (t.pnlIncomplete || t.pnlSource === 'incomplete') return 'incomplete';
  if (t.pnlSource === 'manual_pending') return 'pending review';
  return null;
}
